import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { DatePicker } from '../components/ui/date-picker';
import { confirmDialog } from '../components/ConfirmDialog';

type Fleet = {
  id: string;
  clientId?: string | null;
  name: string;
  cuit?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  stats?: {
    units: number;
    unitsActive: number;
    unitsOutOfService: number;
    pendingAlerts: number;
    overdueAlerts: number;
    openAppointments: number;
  };
};

type FleetVehicle = {
  id: string;
  vehicleId: string;
  licensePlate?: string | null;
  make?: string | null;
  model?: string | null;
  currentMileage?: number | null;
  nextServiceDate?: string | null;
  nextServiceMileage?: number | null;
  insuranceDueDate?: string | null;
  vtvDueDate?: string | null;
  operationalStatus?: string | null;
  priority?: string | null;
};

type Alert = {
  id: string;
  title: string;
  status: string;
  priority: string;
  dueDate?: string | null;
  dueMileage?: number | null;
  licensePlate?: string | null;
};

type WorkOrder = {
  source: string;
  sourceId: string;
  date?: string | null;
  status?: string | null;
  description?: string | null;
  amount?: number | null;
  licensePlate?: string | null;
};

type VehicleOption = {
  id: string;
  licensePlate?: string | null;
  make?: string | null;
  model?: string | null;
  mileage?: number | null;
};

const FleetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [vehicles, setVehicles] = useState<FleetVehicle[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [availableVehicles, setAvailableVehicles] = useState<VehicleOption[]>([]);
  const [editingVehicleId, setEditingVehicleId] = useState<string | null>(null);
  const [vehicleForm, setVehicleForm] = useState({
    vehicleId: '',
    currentMileage: '',
    nextServiceDate: '',
    nextServiceMileage: '',
    insuranceDueDate: '',
    vtvDueDate: '',
    operationalStatus: 'operativa',
    priority: 'media',
  });

  const loadFleet = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await axios.get(`/api/fleets/${id}`);
      setFleet(response.data?.fleet || null);
      setVehicles(response.data?.vehicles || []);
      setAlerts(response.data?.alerts || []);
      setWorkOrders(response.data?.workOrders || []);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo cargar la flota');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadAvailableVehicles = useCallback(async (clientId?: string | null) => {
    if (!clientId) {
      setAvailableVehicles([]);
      return;
    }
    try {
      const response = await axios.get('/api/vehicles', { params: { clientId } });
      setAvailableVehicles((response.data || []) as VehicleOption[]);
    } catch {
      setAvailableVehicles([]);
    }
  }, []);

  useEffect(() => {
    loadFleet();
  }, [loadFleet]);

  useEffect(() => {
    loadAvailableVehicles(fleet?.clientId || null);
  }, [fleet?.clientId, loadAvailableVehicles]);

  const resetVehicleForm = () => {
    setEditingVehicleId(null);
    setVehicleForm({
      vehicleId: '',
      currentMileage: '',
      nextServiceDate: '',
      nextServiceMileage: '',
      insuranceDueDate: '',
      vtvDueDate: '',
      operationalStatus: 'operativa',
      priority: 'media',
    });
  };

  const saveVehicle = async () => {
    if (!id) return;
    if (!vehicleForm.vehicleId) {
      toast.error('Seleccioná una unidad');
      return;
    }
    const payload = {
      vehicleId: vehicleForm.vehicleId,
      currentMileage: vehicleForm.currentMileage,
      nextServiceDate: vehicleForm.nextServiceDate,
      nextServiceMileage: vehicleForm.nextServiceMileage,
      insuranceDueDate: vehicleForm.insuranceDueDate,
      vtvDueDate: vehicleForm.vtvDueDate,
      operationalStatus: vehicleForm.operationalStatus,
      priority: vehicleForm.priority,
    };
    try {
      if (editingVehicleId) await axios.put(`/api/fleets/${id}/vehicles/${editingVehicleId}`, payload);
      else await axios.post(`/api/fleets/${id}/vehicles`, payload);
      toast.success(editingVehicleId ? 'Unidad actualizada' : 'Unidad agregada');
      resetVehicleForm();
      await loadFleet();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo guardar la unidad');
    }
  };

  const startEditVehicle = (v: FleetVehicle) => {
    setEditingVehicleId(v.id);
    setVehicleForm({
      vehicleId: v.vehicleId,
      currentMileage: v.currentMileage ? String(v.currentMileage) : '',
      nextServiceDate: v.nextServiceDate || '',
      nextServiceMileage: v.nextServiceMileage ? String(v.nextServiceMileage) : '',
      insuranceDueDate: v.insuranceDueDate || '',
      vtvDueDate: v.vtvDueDate || '',
      operationalStatus: v.operationalStatus || 'operativa',
      priority: v.priority || 'media',
    });
  };

  const removeVehicle = async (fleetVehicleId: string) => {
    if (!id) return;
    if (!await confirmDialog('Quitar unidad de la flota?')) return;
    try {
      await axios.delete(`/api/fleets/${id}/vehicles/${fleetVehicleId}`);
      toast.success('Unidad removida');
      await loadFleet();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo remover la unidad');
    }
  };

  const recomputeAlerts = async () => {
    if (!id) return;
    try {
      await axios.post(`/api/fleets/${id}/alerts/recompute`);
      toast.success('Alertas recalculadas');
      await loadFleet();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudieron recalcular alertas');
    }
  };

  const resolveAlert = async (alertId: string, status: 'resuelta' | 'descartada') => {
    if (!id) return;
    try {
      await axios.put(`/api/fleets/${id}/alerts/${alertId}`, { status });
      await loadFleet();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo actualizar la alerta');
    }
  };

  const alertCounters = useMemo(() => {
    return alerts.reduce(
      (acc, a) => {
        if (a.status === 'vencida') acc.overdue += 1;
        if (a.status !== 'resuelta' && a.status !== 'descartada') acc.pending += 1;
        return acc;
      },
      { pending: 0, overdue: 0 }
    );
  }, [alerts]);

  if (loading) return <div className="p-6">Cargando...</div>;
  if (!fleet) return <div className="p-6">Flota no encontrada</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{fleet.name}</h1>
          <p className="text-sm text-muted-foreground">{fleet.contactName || '-'} | {fleet.contactPhone || fleet.contactEmail || '-'}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/fleets')}>Volver</Button>
          <Button variant="outline" onClick={() => navigate('/fleets/reports')}>Reportes</Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Unidades</div><div className="text-2xl font-bold">{fleet.stats?.units || vehicles.length}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Operativas</div><div className="text-2xl font-bold">{fleet.stats?.unitsActive || 0}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Alertas pendientes</div><div className="text-2xl font-bold">{alertCounters.pending}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Vencidas</div><div className="text-2xl font-bold text-red-600">{alertCounters.overdue}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Unidades</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 md:grid-cols-4">
            <div className="space-y-1 md:col-span-2">
              <Label>Unidad</Label>
              {availableVehicles.length > 0 ? (
                <select className="h-10 w-full rounded-md border border-input bg-background px-3" value={vehicleForm.vehicleId} onChange={(e) => setVehicleForm((p) => ({ ...p, vehicleId: e.target.value }))}>
                  <option value="">Seleccionar...</option>
                  {availableVehicles.map((v) => (
                    <option key={v.id} value={v.id}>{[v.licensePlate, v.make, v.model].filter(Boolean).join(' - ') || v.id}</option>
                  ))}
                </select>
              ) : (
                <Input placeholder="vehicleId" value={vehicleForm.vehicleId} onChange={(e) => setVehicleForm((p) => ({ ...p, vehicleId: e.target.value }))} />
              )}
            </div>
            <div className="space-y-1"><Label>Km actual</Label><Input value={vehicleForm.currentMileage} onChange={(e) => setVehicleForm((p) => ({ ...p, currentMileage: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Km próximo servicio</Label><Input value={vehicleForm.nextServiceMileage} onChange={(e) => setVehicleForm((p) => ({ ...p, nextServiceMileage: e.target.value }))} /></div>
            <div className="space-y-1"><Label>Servicio fecha</Label><DatePicker value={vehicleForm.nextServiceDate} onChange={(v) => setVehicleForm((p) => ({ ...p, nextServiceDate: v }))} /></div>
            <div className="space-y-1"><Label>VTV vence</Label><DatePicker value={vehicleForm.vtvDueDate} onChange={(v) => setVehicleForm((p) => ({ ...p, vtvDueDate: v }))} /></div>
            <div className="space-y-1"><Label>Seguro vence</Label><DatePicker value={vehicleForm.insuranceDueDate} onChange={(v) => setVehicleForm((p) => ({ ...p, insuranceDueDate: v }))} /></div>
            <div className="space-y-1"><Label>Estado operativo</Label>
              <select className="h-10 w-full rounded-md border border-input bg-background px-3" value={vehicleForm.operationalStatus} onChange={(e) => setVehicleForm((p) => ({ ...p, operationalStatus: e.target.value }))}>
                <option value="operativa">Operativa</option><option value="en_taller">En taller</option><option value="fuera_servicio">Fuera de servicio</option>
              </select>
            </div>
            <div className="space-y-1"><Label>Prioridad</Label>
              <select className="h-10 w-full rounded-md border border-input bg-background px-3" value={vehicleForm.priority} onChange={(e) => setVehicleForm((p) => ({ ...p, priority: e.target.value }))}>
                <option value="baja">Baja</option><option value="media">Media</option><option value="alta">Alta</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={saveVehicle} className="bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-gray-100">{editingVehicleId ? 'Guardar cambios' : 'Agregar unidad'}</Button>
            {editingVehicleId && <Button variant="outline" onClick={resetVehicleForm}>Cancelar</Button>}
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader><TableRow><TableHead>Unidad</TableHead><TableHead>Estado</TableHead><TableHead>Próximos</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
              <TableBody>
                {vehicles.length === 0 && <TableRow><TableCell colSpan={4}>Sin unidades</TableCell></TableRow>}
                {vehicles.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell>
                      <div className="font-medium">{[v.licensePlate, v.make, v.model].filter(Boolean).join(' - ') || v.vehicleId}</div>
                      <div className="text-xs text-muted-foreground">Km actual: {v.currentMileage || '-'}</div>
                    </TableCell>
                    <TableCell><Badge variant="outline">{v.operationalStatus || 'operativa'}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">Serv: {v.nextServiceDate || '-'} | VTV: {v.vtvDueDate || '-'} | Seguro: {v.insuranceDueDate || '-'}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/fleets/${id}/vehicles/${v.id}`)}>Ver</Button>
                      <Button size="sm" variant="outline" onClick={() => startEditVehicle(v)}>Editar</Button>
                      <Button size="sm" variant="destructive" onClick={() => removeVehicle(v.id)}>Quitar</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between"><CardTitle>Alertas</CardTitle><Button variant="outline" onClick={recomputeAlerts}>Recalcular</Button></CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader><TableRow><TableHead>Título</TableHead><TableHead>Vence</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
              <TableBody>
                {alerts.length === 0 && <TableRow><TableCell colSpan={4}>Sin alertas</TableCell></TableRow>}
                {alerts.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      <div className="font-medium">{a.title}</div>
                      <div className="text-xs text-muted-foreground">{a.licensePlate || '-'}</div>
                    </TableCell>
                    <TableCell>{a.dueDate || (a.dueMileage ? `${a.dueMileage} km` : '-')}</TableCell>
                    <TableCell><Badge variant={a.status === 'vencida' ? 'destructive' : 'outline'}>{a.status}</Badge></TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button size="sm" variant="outline" onClick={() => resolveAlert(a.id, 'resuelta')}>Resolver</Button>
                      <Button size="sm" variant="outline" onClick={() => resolveAlert(a.id, 'descartada')}>Descartar</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Trabajos e historial</CardTitle></CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Tipo</TableHead><TableHead>Detalle</TableHead><TableHead>Estado</TableHead><TableHead>Monto</TableHead></TableRow></TableHeader>
              <TableBody>
                {workOrders.length === 0 && <TableRow><TableCell colSpan={5}>Sin movimientos</TableCell></TableRow>}
                {workOrders.map((w, idx) => (
                  <TableRow key={`${w.source}-${w.sourceId}-${idx}`}>
                    <TableCell>{w.date ? String(w.date).slice(0, 10) : '-'}</TableCell>
                    <TableCell><Badge variant="secondary">{w.source}</Badge></TableCell>
                    <TableCell>{w.description || '-'} <span className="text-xs text-muted-foreground">{w.licensePlate ? `(${w.licensePlate})` : ''}</span></TableCell>
                    <TableCell>{w.status || '-'}</TableCell>
                    <TableCell>{w.amount != null ? Number(w.amount).toLocaleString('es-AR') : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FleetDetail;
